import {
  ArrowRight,
  AtSign,
  BadgeCheck,
  BarChart3,
  Globe,
  MousePointerClick,
  Check,
  ChevronRight,
  CircleUserRound,
  Clock3,
  CreditCard,
  Edit3,
  Eye,
  Heart,
  LayoutDashboard,
  KeyRound,
  LockKeyhole,
  LogOut,
  Menu,
  MessageCircle,
  MessageSquare,
  Minus,
  Package,
  PackageCheck,
  Plus,
  Search,
  Send,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Star,
  Store,
  Tag,
  Trash2,
  Truck,
  Upload,
  UserRound,
  Users,
  Video,
  X,
  Zap
} from "lucide-react";
import {
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useState
} from "react";
import { Link, NavLink, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, money, track } from "./api";
import { ErrorBoundary } from "./ErrorBoundary";
import { useStore } from "./store";
import type { AdminUser, AnalyticsSummary, ChatConversation, ChatMessage, Order, Product, StoreSettings, StoreVideo, User, WeeklyReport } from "./types";

const defaultSettings: StoreSettings = {
  store_name: "Descolado & Moderno",
  slogan: "Estilo urbano com atitude",
  whatsapp: "5511999928524",
  instagram: "@dm-descoladoemoderno",
  email: "poncell25@gmail.com",
  address: "Osasco - SP",
  business_hours: "Segunda a sábado, das 8h às 18h",
  origin_cep: "06233350",
  pix_discount_percent: 10,
  installments: 12,
  shipping_national_cents: 2490,
  shipping_local_cents: 1200,
  free_shipping_from_cents: 25000,
  posting_days: 2,
  first_coupon_code: "PRIMEIRA20",
  first_coupon_percent: 20
};

function useCatalog() {
  const [products, setProducts] = useState<Product[]>([]);
  const [videos, setVideos] = useState<StoreVideo[]>([]);
  const [settings, setSettings] = useState<StoreSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = async () => {
    try {
      setError(false);
      const [productData, settingData, videoData] = await Promise.all([
        api.get<{ products: Product[] }>("/api/products"),
        api.get<{ settings: StoreSettings }>("/api/settings"),
        api.get<{ videos: StoreVideo[] }>("/api/videos")
      ]);
      setProducts(productData.products);
      setSettings(settingData.settings);
      setVideos(videoData.videos);
    } catch (caught) {
      console.error("Falha ao carregar a loja:", caught);
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  return { products, videos, settings, loading, error, reload: load };
}

function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <Link className={`brand ${compact ? "brand-compact" : ""}`} to="/" aria-label="Descolado e Moderno">
      <img src="/logo-dm-transparente.png" alt="Descolado & Moderno" />
    </Link>
  );
}

function Loading() {
  return (
    <div className="loading-page">
      <span className="spinner" />
      <p>Preparando a vitrine...</p>
    </div>
  );
}

function Notice({ children, tone = "gold" }: { children: ReactNode; tone?: "gold" | "danger" | "success" }) {
  return <div className={`notice notice-${tone}`}>{children}</div>;
}

function Header({ settings }: { settings: StoreSettings }) {
  const { cartCount, setCartOpen, user } = useStore();
  const [mobile, setMobile] = useState(false);
  const nav = [
    ["/", "Início"],
    ["/produtos?categoria=Tênis", "Tênis"],
    ["/produtos?categoria=Camisetas", "Camisetas"],
    ["/produtos?categoria=Calças", "Calças"],
    ["/produtos?categoria=Blusas de frio", "Blusas de frio"],
    ["/produtos?categoria=Bonés", "Bonés"],
    ["/produtos?categoria=Perfumes", "Perfumes"],
    ["/produtos?categoria=Cintos", "Cintos"]
  ];
  return (
    <header className="site-header">
      <div className="container header-inner">
        <Logo />
        <nav className={mobile ? "nav-open" : ""}>
          {nav.map(([path, label]) => (
            <NavLink key={label} to={path} onClick={() => setMobile(false)}>
              {label}
            </NavLink>
          ))}
          <a href="#contato" onClick={() => setMobile(false)}>Contato</a>
        </nav>
        <div className="header-actions">
          <Link className="icon-button desktop-action" to="/produtos" aria-label="Buscar">
            <Search size={21} />
          </Link>
          <Link className="icon-button desktop-action" to={user ? "/minha-conta" : "/entrar"} aria-label="Minha conta">
            <CircleUserRound size={22} />
          </Link>
          <button className="icon-button cart-button" onClick={() => setCartOpen(true)} aria-label="Abrir carrinho">
            <ShoppingCart size={22} />
            {cartCount > 0 && <span>{cartCount}</span>}
          </button>
          <button className="icon-button mobile-menu" onClick={() => setMobile((value) => !value)} aria-label="Menu">
            {mobile ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      <div className="announcement">
        <span><Zap size={14} /> {settings.pix_discount_percent}% de desconto no Pix</span>
        <span>•</span>
        <span>Cartão em até {settings.installments}x</span>
        <span>•</span>
        <span>Postagem em até {settings.posting_days} dias úteis</span>
      </div>
    </header>
  );
}

function Footer({ settings }: { settings: StoreSettings }) {
  const whatsapp = `https://wa.me/${settings.whatsapp.replace(/\D/g, "")}`;
  return (
    <footer id="contato">
      <div className="container footer-grid">
        <div>
          <Logo />
          <p>Moda urbana escolhida para quem gosta de se vestir com personalidade.</p>
        </div>
        <div>
          <h3>Atendimento</h3>
          <a href={whatsapp} target="_blank" rel="noreferrer" onClick={() => track("click", window.location.pathname, "whatsapp_footer")}><MessageCircle size={17} /> (11) 99992-8524</a>
          <a href={`https://instagram.com/${settings.instagram.replace("@", "")}`} target="_blank" rel="noreferrer">
            <AtSign size={17} /> {settings.instagram}
          </a>
          <a href={`mailto:${settings.email}`}>{settings.email}</a>
        </div>
        <div>
          <h3>Informações</h3>
          <Link to="/politicas">Trocas, devoluções e privacidade</Link>
          <Link to="/minha-conta">Acompanhar pedidos</Link>
          <span>{settings.address}</span>
          <span>{settings.business_hours}</span>
        </div>
        <div>
          <h3>Compra segura</h3>
          <p><ShieldCheck size={18} /> Pagamento processado pelo PagBank.</p>
          <p><CreditCard size={18} /> Pix e cartão em até {settings.installments}x.</p>
        </div>
      </div>
      <div className="container footer-bottom">
        <span>© {new Date().getFullYear()} Descolado & Moderno.</span>
        <span>Feito com atitude.</span>
      </div>
    </footer>
  );
}

const PREORDER_DAYS = 5;

const isPreorderProduct = (product: Product, quantity = 1) =>
  product.stock !== null && product.stock < quantity;

const preorderText = `Disponível por encomenda — prazo de ${PREORDER_DAYS} dias úteis.`;

function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQuantity, removeFromCart } = useStore();
  const subtotal = cart.reduce((sum, item) => sum + item.product.price_cents * item.quantity, 0);
  return (
    <>
      <button className={`drawer-backdrop ${cartOpen ? "show" : ""}`} onClick={() => setCartOpen(false)} aria-label="Fechar carrinho" />
      <aside className={`cart-drawer ${cartOpen ? "show" : ""}`}>
        <div className="drawer-title">
          <div><small>Seu carrinho</small><h2>{cart.length ? `${cart.length} produto(s)` : "Ainda está vazio"}</h2></div>
          <button className="icon-button" onClick={() => setCartOpen(false)}><X /></button>
        </div>
        <div className="drawer-items">
          {cart.length === 0 ? (
            <div className="empty-state">
              <ShoppingBag size={44} />
              <p>Escolha seus favoritos e eles aparecerão aqui.</p>
              <Link className="button button-gold" to="/produtos" onClick={() => setCartOpen(false)}>Ver produtos</Link>
            </div>
          ) : (
            cart.map((item, index) => (
              <div className="cart-line" key={`${item.product.id}-${item.size}-${item.color}`}>
                <img src={item.product.images[0]} alt={item.product.name} />
                <div>
                  <strong>{item.product.name}</strong>
                  <small>{[item.size && `Tam. ${item.size}`, item.color].filter(Boolean).join(" · ")}</small>
                  {isPreorderProduct(item.product, item.quantity) && <small className="preorder-inline">{preorderText}</small>}
                  <span>{money(item.product.price_cents)}</span>
                  <div className="quantity">
                    <button onClick={() => updateQuantity(index, item.quantity - 1)}><Minus size={14} /></button>
                    <b>{item.quantity}</b>
                    <button onClick={() => updateQuantity(index, item.quantity + 1)}><Plus size={14} /></button>
                  </div>
                </div>
                <button className="remove" onClick={() => removeFromCart(index)} aria-label="Remover"><Trash2 size={17} /></button>
              </div>
            ))
          )}
        </div>
        {cart.length > 0 && (
          <div className="drawer-summary">
            <div><span>Subtotal</span><strong>{money(subtotal)}</strong></div>
            <small>Frete e descontos calculados no checkout.</small>
            <Link className="button button-gold button-full" to="/checkout" onClick={() => setCartOpen(false)}>
              Finalizar compra <ArrowRight size={18} />
            </Link>
            <Link className="text-link" to="/carrinho" onClick={() => setCartOpen(false)}>Ver carrinho completo</Link>
          </div>
        )}
      </aside>
    </>
  );
}

function ContactWidget({ settings }: { settings: StoreSettings }) {
  const whatsapp = `https://wa.me/${settings.whatsapp.replace(/\D/g, "")}`;
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"whatsapp" | "chat">("chat");
  const [conversation, setConversation] = useState<ChatConversation | null>(() => {
    try {
      return JSON.parse(localStorage.getItem("dm-chat") || "null") as ChatConversation | null;
    } catch {
      return null;
    }
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [draft, setDraft] = useState("");
  const [feedback, setFeedback] = useState("");
  const [sending, setSending] = useState(false);
  const loadMessages = async () => {
    if (!conversation?.id || !conversation.token) return;
    const data = await api.get<{ messages: ChatMessage[] }>(
      `/api/chat/conversations/${conversation.id}/messages?token=${encodeURIComponent(conversation.token)}`
    );
    setMessages(data.messages);
  };
  useEffect(() => {
    if (!open || !conversation?.id) return;
    void loadMessages();
    const timer = window.setInterval(() => void loadMessages(), 8000);
    return () => window.clearInterval(timer);
  }, [open, conversation?.id]);
  const startChat = async (event: FormEvent) => {
    event.preventDefault(); setSending(true); setFeedback("");
    try {
      const data = await api.post<{ conversation: ChatConversation; messages: ChatMessage[] }>("/api/chat/conversations", form);
      setConversation(data.conversation);
      setMessages(data.messages);
      localStorage.setItem("dm-chat", JSON.stringify(data.conversation));
      setForm({ ...form, message: "" });
    } catch (caught) { setFeedback(caught instanceof Error ? caught.message : "Não foi possível iniciar o chat."); }
    finally { setSending(false); }
  };
  const sendMessage = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft.trim() || !conversation?.id || !conversation.token) return;
    setSending(true); setFeedback("");
    try {
      await api.post(`/api/chat/conversations/${conversation.id}/messages`, { token: conversation.token, message: draft });
      setDraft("");
      await loadMessages();
    } catch (caught) { setFeedback(caught instanceof Error ? caught.message : "Não foi possível enviar."); }
    finally { setSending(false); }
  };
  return (
    <div className="contact-widget">
      {open && <div className="contact-panel">
        <div className="contact-panel-title">
          <div><small>Fale conosco</small><strong>Atendimento DM</strong></div>
          <button className="icon-button" onClick={() => setOpen(false)}><X /></button>
        </div>
        <div className="contact-tabs">
          <button className={mode === "chat" ? "active" : ""} onClick={() => setMode("chat")}><MessageSquare size={16} /> Chat</button>
          <button className={mode === "whatsapp" ? "active" : ""} onClick={() => setMode("whatsapp")}><MessageCircle size={16} /> WhatsApp</button>
        </div>
        {mode === "whatsapp" ? (
          <div className="contact-whatsapp">
            <p>Prefere conversar direto pelo WhatsApp? Clique abaixo e chame a loja.</p>
            <a className="button button-gold button-full" href={whatsapp} target="_blank" rel="noreferrer" onClick={() => track("click", window.location.pathname, "whatsapp_widget")}>
              Abrir WhatsApp <MessageCircle size={17} />
            </a>
          </div>
        ) : conversation ? (
          <div className="chat-box">
            <div className="chat-messages">
              {messages.map((message) => <div key={message.id} className={`chat-bubble ${message.sender_type}`}>
                <small>{message.sender_name}</small><p>{message.body}</p>
              </div>)}
              {!messages.length && <p className="chat-empty">Sua conversa aparecerá aqui.</p>}
            </div>
            <form className="chat-send" onSubmit={sendMessage}>
              <input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Digite sua mensagem..." />
              <button disabled={sending || !draft.trim()}><Send size={17} /></button>
            </form>
          </div>
        ) : (
          <form className="contact-form" onSubmit={startChat}>
            <label>Nome<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
            <label>WhatsApp<input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
            <label>E-mail<input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
            <label>Mensagem<textarea required value={form.message} onChange={(event) => setForm({ ...form, message: event.target.value })} /></label>
            {feedback && <Notice tone="danger">{feedback}</Notice>}
            <button className="button button-gold button-full" disabled={sending}>{sending ? "Enviando..." : "Iniciar chat"}</button>
          </form>
        )}
        {feedback && conversation && <Notice tone="danger">{feedback}</Notice>}
      </div>}
      <button className="whatsapp-float" onClick={() => setOpen((value) => !value)}>
        <MessageCircle />
        <span>Fale conosco</span>
      </button>
    </div>
  );
}

function Shell({ settings }: { settings: StoreSettings }) {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);
  useEffect(() => {
    track("pageview", location.pathname);
  }, [location.pathname]);
  return (
    <>
      <Header settings={settings} />
      <main><ErrorBoundary resetKey={location.pathname}><Outlet /></ErrorBoundary></main>
      <Footer settings={settings} />
      <CartDrawer />
      <ContactWidget settings={settings} />
    </>
  );
}

function ProductCard({ product }: { product: Product }) {
  const { addToCart, favorites, toggleFavorite } = useStore();
  const favorite = favorites.includes(product.id);
  const preorder = isPreorderProduct(product);
  return (
    <article className="product-card">
      <div className="product-image-wrap">
        <Link to={`/produto/${product.slug}`}>
          <img src={product.images[0]} alt={product.name} loading="lazy" />
        </Link>
        <button className={favorite ? "favorite active" : "favorite"} onClick={() => toggleFavorite(product.id)} aria-label="Favoritar">
          <Heart size={19} fill={favorite ? "currentColor" : "none"} />
        </button>
        {(preorder || product.featured) && <span className="card-badge">{preorder ? "Encomenda" : "Destaque"}</span>}
      </div>
      <div className="product-card-body">
        <small>{product.category}</small>
        <Link to={`/produto/${product.slug}`}><h3>{product.name}</h3></Link>
        <div className="variant-chips">
          {product.sizes.slice(0, 3).map((size) => <span key={size}>{size === "Único" ? "Tamanho único" : `Tam. ${size}`}</span>)}
        </div>
        {preorder && <p className="preorder-note">{preorderText}</p>}
        <div className="price-row">
          <div>
            {product.compare_at_cents && <del>{money(product.compare_at_cents)}</del>}
            <strong>{money(product.price_cents)}</strong>
          </div>
          <button
            className="mini-cart"
            onClick={() => addToCart(product, { size: product.sizes[0], color: product.colors[0] })}
            aria-label="Adicionar ao carrinho"
          >
            <ShoppingCart size={18} />
          </button>
        </div>
        <Link className="product-link" to={`/produto/${product.slug}`}>Ver produto <ChevronRight size={16} /></Link>
      </div>
    </article>
  );
}

function videoEmbedUrl(video: StoreVideo) {
  if (video.source_type === "youtube" || video.url.includes("youtube.com") || video.url.includes("youtu.be")) {
    const match = video.url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{6,})/);
    return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&mute=1&loop=1&playlist=${match[1]}&controls=0&playsinline=1&modestbranding=1&rel=0` : video.url;
  }
  if (video.source_type === "vimeo" || video.url.includes("vimeo.com")) {
    const match = video.url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return match ? `https://player.vimeo.com/video/${match[1]}?autoplay=1&muted=1&loop=1&background=1&controls=0` : video.url;
  }
  return "";
}

function AmbientVideo({ video, featured = false }: { video: StoreVideo; featured?: boolean }) {
  const embed = videoEmbedUrl(video);
  return <div className={`video-frame ambient ${featured ? "featured" : ""}`}>
    {embed ? (
      <iframe src={embed} title={video.title} allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen />
    ) : (
      <video autoPlay muted loop playsInline preload="auto" poster={video.poster_url || undefined} src={video.url} />
    )}
    <div className="video-shade" />
  </div>;
}

function VideosSection({ videos }: { videos: StoreVideo[] }) {
  if (!videos.length) return null;
  const [featured, ...rest] = videos;
  return <section className="section videos-section"><div className="container">
    <div className="section-title"><div><span className="eyebrow"><Video size={16} /> Vídeos</span><h2>DM em movimento</h2></div></div>
    <article className="video-showcase">
      <AmbientVideo video={featured} featured />
      <div className="video-showcase-copy">
        <span className="eyebrow">Assista</span>
        <h3>{featured.title}</h3>
        <p>{featured.description || "Bastidores, novidades e o estilo da Descolado & Moderno em destaque."}</p>
      </div>
    </article>
    {rest.length > 0 && <div className="video-grid">{rest.map((video) => <article className="video-card" key={video.id}>
      <AmbientVideo video={video} />
      <div><small>{video.source_type}</small><h3>{video.title}</h3>{video.description && <p>{video.description}</p>}</div>
    </article>)}</div>}
  </div></section>;
}

function Home({ products, settings, videos }: { products: Product[]; settings: StoreSettings; videos: StoreVideo[] }) {
  const featured = products.filter((product) => product.featured).slice(0, 8);
  return (
    <>
      <section className="hero">
        <div className="hero-background" />
        <div className="container hero-content">
          <span className="eyebrow"><Sparkles size={16} /> DM Descolado & Moderno</span>
          <h1>Estilo urbano<br /><em>com atitude</em></h1>
          <p>Os melhores tênis, camisetas e acessórios para quem vive o streetwear.</p>
          <div className="hero-actions">
            <Link className="button button-gold" to="/produtos" onClick={() => track("click", "/", "hero_comprar_agora")}>Comprar agora <ArrowRight size={18} /></Link>
            <a className="button button-ghost" href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noreferrer" onClick={() => track("click", "/", "whatsapp_hero")}>
              <MessageCircle size={18} /> Chamar no WhatsApp
            </a>
          </div>
        </div>
      </section>
      <section className="benefits">
        <div className="container benefits-grid">
          <div><CreditCard /><span><small>Aceitamos</small><strong>Pix e Cartão</strong></span></div>
          <div><Truck /><span><small>Entrega para</small><strong>todo o Brasil</strong></span></div>
          <div><Tag /><span><small>Cupom de primeira compra</small><strong>{settings.first_coupon_percent}% OFF</strong></span></div>
          <div><MessageCircle /><span><small>Atendimento</small><strong>via WhatsApp</strong></span></div>
        </div>
      </section>
      <section className="section">
        <div className="container">
          <div className="section-title">
            <div><span className="eyebrow">Produtos em destaque</span><h2>Selecionados para você</h2></div>
            <Link className="button button-outline" to="/produtos">Ver todos <ArrowRight size={17} /></Link>
          </div>
          <div className="product-grid">{featured.map((product) => <ProductCard key={product.id} product={product} />)}</div>
        </div>
      </section>
      <section className="about-section">
        <div className="container about-grid">
          <div className="about-visual">
            <img className="about-visual-logo" src="/logo-dm-transparente.png" alt="Descolado & Moderno" />
          </div>
          <div className="about-copy">
            <span className="eyebrow">Conheça a DM</span>
            <h2>Mais que uma loja,<br /><em>um estilo de vida.</em></h2>
            <p>A Descolado & Moderno nasceu da paixão pelo streetwear e pela cultura urbana. Produtos escolhidos com cuidado e atendimento próximo, do nosso jeito.</p>
            <div className="about-points">
              <div><BadgeCheck /><span><b>Curadoria</b><small>Selecionada</small></span></div>
              <div><ShieldCheck /><span><b>Compra</b><small>Segura</small></span></div>
              <div><UserRound /><span><b>Atendimento</b><small>Personalizado</small></span></div>
            </div>
            <a className="button button-outline" href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noreferrer">Conhecer a DM <ArrowRight size={17} /></a>
          </div>
        </div>
      </section>
      <section className="coupon-banner">
        <div className="container">
          <div><span className="eyebrow">Sua primeira compra</span><h2>Ganhe {settings.first_coupon_percent}% de desconto</h2><p>Use o cupom <b>{settings.first_coupon_code}</b> no checkout.</p></div>
          <Link className="button button-light" to="/produtos">Escolher meus produtos <ArrowRight size={18} /></Link>
        </div>
      </section>
      <VideosSection videos={videos} />
      <section className="dev-banner">
        <div className="container dev-banner-inner">
          <div><span className="eyebrow">Gostou do site?</span><h2>Quer montar um site igual para sua loja?</h2><p>Fale com <a href="mailto:poncell25@gmail.com">poncell25@gmail.com</a> ou chame no WhatsApp.</p></div>
          <a className="button button-gold" href="https://wa.me/5511973808845" target="_blank" rel="noreferrer" onClick={() => track("click", "/", "whatsapp_dev_banner")}><MessageCircle size={18} /> Chamar no WhatsApp</a>
        </div>
      </section>
    </>
  );
}

function Catalog({ products }: { products: Product[] }) {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("busca") || "");
  const category = params.get("categoria") || "Todos";
  const categories = [
    "Todos",
    "Tênis",
    "Camisetas",
    "Calças",
    "Blusas de frio",
    "Bonés",
    "Perfumes",
    "Cintos",
    "Acessórios"
  ];
  const filtered = products.filter((product) => {
    const matchCategory = category === "Todos" || product.category === category;
    const term = search.trim().toLowerCase();
    return matchCategory && (!term || `${product.name} ${product.category} ${product.description}`.toLowerCase().includes(term));
  });
  return (
    <section className="section catalog-page">
      <div className="container">
        <div className="page-heading">
          <span className="eyebrow">Catálogo completo</span>
          <h1>Encontre seu próximo estilo</h1>
          <p>Tênis, camisetas e acessórios escolhidos para você.</p>
        </div>
        <div className="catalog-tools">
          <label className="search-box"><Search size={19} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar produto..." /></label>
          <div className="category-pills">
            {categories.map((item) => (
              <button
                className={category === item ? "active" : ""}
                key={item}
                onClick={() => {
                  const next = new URLSearchParams(params);
                  item === "Todos" ? next.delete("categoria") : next.set("categoria", item);
                  setParams(next);
                }}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        <div className="results-count">{filtered.length} produto(s) encontrado(s)</div>
        {filtered.length ? <div className="product-grid">{filtered.map((product) => <ProductCard key={product.id} product={product} />)}</div> : (
          <div className="empty-state"><Search size={42} /><h2>Nenhum produto encontrado</h2><p>Tente outro termo ou categoria.</p></div>
        )}
      </div>
    </section>
  );
}

function ProductDetail({ products, settings }: { products: Product[]; settings: StoreSettings }) {
  const { slug } = useParams();
  const product = products.find((item) => item.slug === slug);
  const { addToCart, favorites, toggleFavorite } = useStore();
  const [size, setSize] = useState(product?.sizes[0] || "");
  const [color, setColor] = useState(product?.colors[0] || "");
  const [quantity, setQuantity] = useState(1);
  const preorder = product ? isPreorderProduct(product, quantity) : false;
  if (!product) return <div className="container empty-state section"><Package size={42} /><h2>Produto não encontrado</h2><Link className="button button-gold" to="/produtos">Voltar ao catálogo</Link></div>;
  return (
    <section className="section product-page">
      <div className="container product-detail">
        <div className="product-gallery"><img src={product.images[0]} alt={product.name} /></div>
        <div className="product-info">
          <span className="eyebrow">{product.category}</span>
          <h1>{product.name}</h1>
          <div className="rating"><span><Star size={16} fill="currentColor" /><Star size={16} fill="currentColor" /><Star size={16} fill="currentColor" /><Star size={16} fill="currentColor" /><Star size={16} fill="currentColor" /></span> Produto selecionado pela DM</div>
          <div className="product-price">{product.compare_at_cents && <del>{money(product.compare_at_cents)}</del>}<strong>{money(product.price_cents)}</strong><small>ou em até {settings.installments}x no cartão</small><b>{money(Math.round(product.price_cents * (1 - settings.pix_discount_percent / 100)))} no Pix</b></div>
          <p className="product-description">{product.description}</p>
          {preorder && <Notice>Produto disponível por encomenda. Após a confirmação do pagamento, o prazo estimado para entrega é de <b>{PREORDER_DAYS} dias úteis</b>.</Notice>}
          {product.sizes.length > 0 && <div className="option-group"><label>Tamanho: <b>{size}</b></label><div>{product.sizes.map((item) => <button className={size === item ? "active" : ""} onClick={() => setSize(item)} key={item}>{item}</button>)}</div></div>}
          {product.colors.length > 0 && <div className="option-group"><label>Cor: <b>{color}</b></label><div>{product.colors.map((item) => <button className={color === item ? "active" : ""} onClick={() => setColor(item)} key={item}>{item}</button>)}</div></div>}
          <div className="buy-row">
            <div className="quantity large"><button onClick={() => setQuantity(Math.max(1, quantity - 1))}><Minus /></button><b>{quantity}</b><button onClick={() => setQuantity(Math.min(10, quantity + 1))}><Plus /></button></div>
            <button className="button button-gold button-grow" onClick={() => addToCart(product, { size, color, quantity })}><ShoppingCart size={19} /> {preorder ? "Encomendar" : "Adicionar ao carrinho"}</button>
            <button className={`heart-large ${favorites.includes(product.id) ? "active" : ""}`} onClick={() => toggleFavorite(product.id)}><Heart fill={favorites.includes(product.id) ? "currentColor" : "none"} /></button>
          </div>
          <div className="assurances">
            <div><ShieldCheck /><span><b>Pagamento seguro</b><small>Processado pelo PagBank</small></span></div>
            <div><Truck /><span><b>Envio nacional</b><small>Postagem em até {settings.posting_days} dias úteis</small></span></div>
            {preorder && <div><Clock3 /><span><b>Encomenda</b><small>Prazo de {PREORDER_DAYS} dias úteis após pagamento</small></span></div>}
            <div><MessageCircle /><span><b>Precisa de ajuda?</b><small>Chame a gente no WhatsApp</small></span></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CartPage() {
  const { cart, updateQuantity, removeFromCart } = useStore();
  const subtotal = cart.reduce((sum, item) => sum + item.product.price_cents * item.quantity, 0);
  if (!cart.length) return <section className="container section empty-state"><ShoppingBag size={52} /><h1>Seu carrinho está vazio</h1><p>Tem muita coisa bonita esperando por você.</p><Link className="button button-gold" to="/produtos">Explorar produtos</Link></section>;
  return (
    <section className="section">
      <div className="container">
        <div className="page-heading left"><span className="eyebrow">Seu pedido</span><h1>Carrinho de compras</h1></div>
        <div className="cart-page-grid">
          <div className="cart-table">
            {cart.map((item, index) => <div className="cart-page-line" key={`${item.product.id}-${item.size}-${item.color}`}>
              <img src={item.product.images[0]} alt={item.product.name} />
              <div><small>{item.product.category}</small><Link to={`/produto/${item.product.slug}`}><h3>{item.product.name}</h3></Link><p>{[item.size && `Tam. ${item.size}`, item.color].filter(Boolean).join(" · ")}</p></div>
              {isPreorderProduct(item.product, item.quantity) && <div className="preorder-cart-note">{preorderText}</div>}
              <div className="quantity"><button onClick={() => updateQuantity(index, item.quantity - 1)}><Minus size={14} /></button><b>{item.quantity}</b><button onClick={() => updateQuantity(index, item.quantity + 1)}><Plus size={14} /></button></div>
              <strong>{money(item.product.price_cents * item.quantity)}</strong>
              <button className="remove" onClick={() => removeFromCart(index)}><Trash2 size={18} /></button>
            </div>)}
          </div>
          <aside className="order-summary"><h2>Resumo</h2><div><span>Subtotal</span><strong>{money(subtotal)}</strong></div><div><span>Frete</span><span>Calculado no checkout</span></div><hr /><div className="summary-total"><span>Total parcial</span><strong>{money(subtotal)}</strong></div><Link className="button button-gold button-full" to="/checkout">Ir para o checkout <ArrowRight size={18} /></Link><Link className="text-link" to="/produtos">Continuar comprando</Link></aside>
        </div>
      </div>
    </section>
  );
}

type CheckoutForm = {
  name: string; email: string; phone: string; tax_id: string; cep: string; street: string; number: string;
  complement: string; neighborhood: string; city: string; state: string; notes: string;
};

type CardForm = {
  number: string; holder_name: string; exp_month: string; exp_year: string; security_code: string; installments: number;
};

function Checkout({ settings }: { settings: StoreSettings }) {
  const { cart, user } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState<CheckoutForm>({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "", tax_id: "", cep: "", street: "", number: "",
    complement: "", neighborhood: "", city: "", state: "", notes: ""
  });
  const [card, setCard] = useState<CardForm>({
    number: "", holder_name: "", exp_month: "", exp_year: "", security_code: "", installments: 1
  });
  const [shipping, setShipping] = useState("national");
  const [payment, setPayment] = useState("pix");
  const [coupon, setCoupon] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const usesSplitCheckout = settings.active_payment_provider === "pagbank_split";
  const changeCard = (key: keyof CardForm) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setCard((current) => ({ ...current, [key]: key === "installments" ? Number(event.target.value) : event.target.value }));
  const subtotal = cart.reduce((sum, item) => sum + item.product.price_cents * item.quantity, 0);
  const couponDiscount = couponApplied ? Math.round(subtotal * settings.first_coupon_percent / 100) : 0;
  const pixDiscount = payment === "pix" ? Math.round((subtotal - couponDiscount) * settings.pix_discount_percent / 100) : 0;
  const shippingCost = shipping === "pickup" ? 0 : shipping === "local" ? settings.shipping_local_cents : subtotal >= settings.free_shipping_from_cents ? 0 : settings.shipping_national_cents;
  const total = subtotal - couponDiscount - pixDiscount + shippingCost;
  const hasPreorder = cart.some((item) => isPreorderProduct(item.product, item.quantity));
  const change = (key: keyof CheckoutForm) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const lookupCep = async () => {
    const cep = form.cep.replace(/\D/g, "");
    if (cep.length !== 8) return;
    try {
      const data = await fetch(`https://viacep.com.br/ws/${cep}/json/`).then((response) => response.json()) as {
        erro?: boolean; logradouro?: string; bairro?: string; localidade?: string; uf?: string;
      };
      if (!data.erro) setForm((current) => ({ ...current, street: data.logradouro || "", neighborhood: data.bairro || "", city: data.localidade || "", state: data.uf || "" }));
    } catch { /* preenchimento manual continua disponível */ }
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true); setMessage("");
    try {
      let cardPayload: { encrypted: string; holder_name: string; holder_tax_id: string; installments: number } | undefined;
      if (usesSplitCheckout && payment === "credit_card") {
        if (!window.PagSeguro) throw new Error("Não foi possível carregar o módulo seguro de cartão. Recarregue a página.");
        const { public_key } = await api.get<{ public_key: string }>("/api/payments/pagbank-public-key");
        const encrypted = window.PagSeguro.encryptCard({
          publicKey: public_key,
          holder: card.holder_name || form.name,
          number: card.number.replace(/\D/g, ""),
          expMonth: card.exp_month,
          expYear: card.exp_year,
          securityCode: card.security_code
        });
        if (encrypted.hasErrors || !encrypted.encryptedCard) {
          throw new Error(encrypted.errors?.[0]?.message || "Confira os dados do cartão.");
        }
        cardPayload = {
          encrypted: encrypted.encryptedCard,
          holder_name: card.holder_name || form.name,
          holder_tax_id: form.tax_id,
          installments: card.installments
        };
      }
      const result = await api.post<{
        order_code: string;
        payment_status: "paid" | "pending" | "declined";
        payment_url: string | null;
        pix: { text: string; image_base64: string | null; expires_at: string | null } | null;
        warning?: string;
      }>("/api/orders", {
        customer: { name: form.name, email: form.email, phone: form.phone, tax_id: form.tax_id },
        address: { cep: form.cep.replace(/\D/g, ""), street: form.street, number: form.number, complement: form.complement, neighborhood: form.neighborhood, city: form.city, state: form.state },
        items: cart.map((item) => ({ product_id: item.product.id, quantity: item.quantity, size: item.size, color: item.color })),
        coupon_code: couponApplied ? settings.first_coupon_code : undefined,
        shipping_method: shipping,
        payment_method: payment,
        notes: form.notes,
        card: cardPayload
      });
      if (result.payment_url) {
        window.location.assign(result.payment_url);
      } else if (result.pix) {
        navigate(`/pedido-confirmado?order_nsu=${result.order_code}`, { state: { pix: result.pix } });
      } else if (result.payment_status === "paid") {
        navigate(`/pedido-confirmado?order_nsu=${result.order_code}`);
      } else if (result.warning?.toLowerCase().includes("demonstração")) {
        navigate(`/pedido-confirmado?order_nsu=${result.order_code}&demo=1&warning=${encodeURIComponent(result.warning || "")}`);
      } else {
        setMessage(result.warning || "Pagamento não aprovado. Revise os dados do cartão e tente novamente.");
      }
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Não foi possível criar o pedido.");
    } finally { setSubmitting(false); }
  };
  if (!cart.length) return <Navigate to="/carrinho" replace />;
  return (
    <section className="section checkout-page">
      <div className="container">
        <div className="page-heading left"><span className="eyebrow">Finalizar compra</span><h1>Checkout seguro</h1><p>Seus dados são usados somente para processar e entregar o pedido.</p></div>
        <form className="checkout-grid" onSubmit={submit}>
          <div className="checkout-forms">
            <div className="checkout-card"><div className="checkout-card-title"><span>1</span><div><h2>Seus dados</h2><p>Para contato e confirmação do pedido.</p></div></div><div className="form-grid">
              <label className="span-2">Nome completo<input required value={form.name} onChange={change("name")} /></label>
              <label>E-mail<input required type="email" value={form.email} onChange={change("email")} /></label>
              <label>WhatsApp<input required value={form.phone} onChange={change("phone")} placeholder="(11) 99999-9999" /></label>
              {usesSplitCheckout && <label>CPF<input required value={form.tax_id} onChange={change("tax_id")} placeholder="000.000.000-00" /></label>}
            </div></div>
            <div className="checkout-card"><div className="checkout-card-title"><span>2</span><div><h2>Forma de entrega</h2><p>Escolha a opção mais conveniente.</p></div></div>
              <div className="choice-list">
                <button type="button" className={shipping === "national" ? "choice active" : "choice"} onClick={() => setShipping("national")}><Truck /><span><b>Envio para todo o Brasil</b><small>Valor fixo inicial; prazo informado após a postagem.</small></span><strong>{subtotal >= settings.free_shipping_from_cents ? "Grátis" : money(settings.shipping_national_cents)}</strong></button>
                <button type="button" className={shipping === "local" ? "choice active" : "choice"} onClick={() => setShipping("local")}><PackageCheck /><span><b>Entrega local</b><small>Consulte cobertura para Osasco e região.</small></span><strong>{money(settings.shipping_local_cents)}</strong></button>
                <button type="button" className={shipping === "pickup" ? "choice active" : "choice"} onClick={() => setShipping("pickup")}><Store /><span><b>Retirar na loja</b><small>Aguarde a confirmação de que o pedido está pronto.</small></span><strong>Grátis</strong></button>
              </div>
              {shipping !== "pickup" && <div className="form-grid address-grid">
                <label>CEP<input required value={form.cep} onBlur={lookupCep} onChange={change("cep")} /></label><span />
                <label className="span-2">Rua<input required value={form.street} onChange={change("street")} /></label>
                <label>Número<input required value={form.number} onChange={change("number")} /></label><label>Complemento<input value={form.complement} onChange={change("complement")} /></label>
                <label>Bairro<input required value={form.neighborhood} onChange={change("neighborhood")} /></label><label>Cidade/UF<input required value={`${form.city}${form.state ? `/${form.state}` : ""}`} onChange={(event) => setForm((current) => ({ ...current, city: event.target.value }))} /></label>
              </div>}
            </div>
            <div className="checkout-card"><div className="checkout-card-title"><span>3</span><div><h2>Pagamento</h2><p>{usesSplitCheckout ? "Processado com segurança pelo PagBank." : "Você finalizará no ambiente seguro da InfinitePay."}</p></div></div>
              <div className="payment-choices">
                <button type="button" className={payment === "pix" ? "payment-choice active" : "payment-choice"} onClick={() => setPayment("pix")}><Zap /><span><b>Pix</b><small>{settings.pix_discount_percent}% de desconto</small></span><Check /></button>
                <button type="button" className={payment === "credit_card" ? "payment-choice active" : "payment-choice"} onClick={() => setPayment("credit_card")}><CreditCard /><span><b>Cartão de crédito</b><small>Em até {settings.installments}x</small></span><Check /></button>
              </div>
              {payment === "pix" && <Notice>{usesSplitCheckout ? "O QR Code Pix aparece na próxima tela, com o desconto já aplicado." : "O checkout será aberto diretamente na opção Pix, com o desconto aplicado."}</Notice>}
              {usesSplitCheckout && payment === "credit_card" && <div className="form-grid card-grid">
                <label className="span-2">Número do cartão<input required value={card.number} onChange={changeCard("number")} placeholder="0000 0000 0000 0000" /></label>
                <label className="span-2">Nome impresso no cartão<input required value={card.holder_name} onChange={changeCard("holder_name")} placeholder={form.name} /></label>
                <label>Mês<input required value={card.exp_month} onChange={changeCard("exp_month")} placeholder="MM" maxLength={2} /></label>
                <label>Ano<input required value={card.exp_year} onChange={changeCard("exp_year")} placeholder="AAAA" maxLength={4} /></label>
                <label>CVV<input required value={card.security_code} onChange={changeCard("security_code")} placeholder="123" maxLength={4} /></label>
                <label>Parcelas<select value={card.installments} onChange={changeCard("installments")}>
                  {Array.from({ length: settings.installments }, (_, index) => index + 1).map((n) => <option key={n} value={n}>{n}x</option>)}
                </select></label>
              </div>}
            </div>
            <div className="checkout-card"><label>Observações do pedido<textarea value={form.notes} onChange={change("notes")} placeholder="Ex.: preferência de modelo do cinto..." /></label></div>
          </div>
          <aside className="checkout-summary">
            <h2>Resumo do pedido</h2>
            <div className="summary-products">{cart.map((item) => <div key={`${item.product.id}-${item.size}`}><img src={item.product.images[0]} alt="" /><span><b>{item.product.name}</b><small>{item.quantity} × {money(item.product.price_cents)}</small></span><strong>{money(item.quantity * item.product.price_cents)}</strong></div>)}</div>
            <div className="coupon-box"><input value={coupon} onChange={(event) => setCoupon(event.target.value.toUpperCase())} placeholder="Cupom de desconto" /><button type="button" onClick={() => { const valid = coupon.trim().toUpperCase() === settings.first_coupon_code.toUpperCase(); setCouponApplied(valid); setMessage(valid ? "" : "Confira o código do cupom."); }}>{couponApplied ? <Check size={17} /> : "Aplicar"}</button></div>
            <div className="totals"><div><span>Subtotal</span><strong>{money(subtotal)}</strong></div>{couponDiscount > 0 && <div className="discount"><span>Cupom {settings.first_coupon_code}</span><strong>- {money(couponDiscount)}</strong></div>}{pixDiscount > 0 && <div className="discount"><span>Desconto Pix</span><strong>- {money(pixDiscount)}</strong></div>}<div><span>Entrega</span><strong>{shippingCost ? money(shippingCost) : "Grátis"}</strong></div><hr /><div className="grand-total"><span>Total</span><strong>{money(total)}</strong></div></div>
            {message && <Notice tone="danger">{message}</Notice>}
            <button className="button button-gold button-full checkout-button" disabled={submitting}>{submitting ? <><span className="spinner small" /> Processando...</> : <>Finalizar pedido <LockKeyhole size={17} /></>}</button>
            <p className="secure-note"><ShieldCheck size={16} /> {usesSplitCheckout ? "Pagamento e dados de cartão processados pelo PagBank." : "Pagamento processado no ambiente seguro da InfinitePay."}</p>
            {hasPreorder && <p className="preorder-checkout-note">Produto(s) por encomenda: prazo de {PREORDER_DAYS} dias úteis após confirmação do pagamento.</p>}
          </aside>
        </form>
      </div>
    </section>
  );
}

function AuthPage() {
  const [register, setRegister] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { refreshUser } = useStore();
  const navigate = useNavigate();
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setLoading(true); setMessage("");
    try {
      await api.post(register ? "/api/auth/register" : "/api/auth/login", form);
      await refreshUser(); navigate("/minha-conta");
    } catch (caught) { setMessage(caught instanceof Error ? caught.message : "Erro ao entrar."); }
    finally { setLoading(false); }
  };
  return <section className="section auth-section"><div className="auth-card"><Logo /><span className="eyebrow">{register ? "Criar conta" : "Bem-vindo de volta"}</span><h1>{register ? "Faça parte da DM" : "Entre na sua conta"}</h1><p>{register ? "Acompanhe pedidos e compre mais rápido." : "Acesse seus pedidos e dados."}</p>
    <form onSubmit={submit}>{register && <><label>Nome<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label><label>WhatsApp<input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label></>}<label>E-mail<input required type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label><label>Senha<input required minLength={8} type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} /></label>{message && <Notice tone="danger">{message}</Notice>}<button className="button button-gold button-full" disabled={loading}>{loading ? "Aguarde..." : register ? "Criar minha conta" : "Entrar"}</button></form>
    <button className="auth-switch" onClick={() => { setRegister(!register); setMessage(""); }}>{register ? "Já tenho conta" : "Ainda não tenho conta"}</button>
  </div></section>;
}

const orderStatus: Record<string, string> = {
  awaiting_payment: "Aguardando pagamento", preparing: "Em preparação", shipped: "Enviado",
  ready_for_pickup: "Pronto para retirada", completed: "Concluído", cancelled: "Cancelado"
};

function AccountPage() {
  const { user, setUser } = useStore();
  const [orders, setOrders] = useState<Order[]>([]);
  const navigate = useNavigate();
  useEffect(() => { if (user) api.get<{ orders: Order[] }>("/api/orders/me").then((data) => setOrders(data.orders)).catch(() => undefined); }, [user]);
  if (!user) return <Navigate to="/entrar" replace />;
  const logout = async () => { await api.post("/api/auth/logout"); setUser(null); navigate("/"); };
  return <section className="section account-page"><div className="container"><div className="account-heading"><div><span className="eyebrow">Minha conta</span><h1>Olá, {user.name.split(" ")[0]}</h1><p>{user.email}</p></div><button className="button button-outline" onClick={logout}><LogOut size={17} /> Sair</button></div>
    {user.role === "admin" && <Link className="admin-callout" to="/admin"><LayoutDashboard /><span><b>Acessar painel administrativo</b><small>Produtos, pedidos e configurações da loja.</small></span><ArrowRight /></Link>}
    <div className="section-title compact"><div><h2>Meus pedidos</h2></div></div>
    {!orders.length ? <div className="empty-state panel"><Package size={42} /><h2>Você ainda não possui pedidos</h2><Link className="button button-gold" to="/produtos">Começar a comprar</Link></div> : <div className="orders-list">{orders.map((order) => <article key={order.id}><div><span>Pedido <b>{order.order_code}</b></span><small>{new Date(order.created_at).toLocaleDateString("pt-BR")}</small></div><div className={`status status-${order.order_status}`}>{orderStatus[order.order_status] || order.order_status}</div><div><span>{order.items.length} item(ns)</span><strong>{money(order.total_cents)}</strong></div>{order.receipt_url && <a href={order.receipt_url} target="_blank" rel="noreferrer">Ver comprovante</a>}</article>)}</div>}
  </div></section>;
}

function ConfirmationPage({ settings }: { settings: StoreSettings }) {
  const [params] = useSearchParams();
  const location = useLocation();
  const [status, setStatus] = useState<"checking" | "paid" | "pending" | "demo">("checking");
  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const { clearCart } = useStore();
  const order = params.get("order_nsu") || "";
  const pix = (location.state as { pix?: { text: string; image_base64: string | null; expires_at: string | null } } | null)?.pix || null;
  useEffect(() => { if (order) clearCart(); }, [order]);
  useEffect(() => {
    if (params.get("demo")) { setStatus("demo"); setMessage(params.get("warning") || "Modo de demonstração."); return; }
    if (!order) { setStatus("pending"); return; }
    let active = true;
    const check = () =>
      api.post<{ paid: boolean }>("/api/payments/verify", { order_nsu: order })
        .then((data) => { if (active) setStatus(data.paid ? "paid" : "pending"); })
        .catch((caught) => { if (active) { setStatus("pending"); setMessage(caught instanceof Error ? caught.message : "Aguardando confirmação."); } });
    check();
    const interval = pix ? setInterval(check, 4000) : undefined;
    return () => { active = false; if (interval) clearInterval(interval); };
  }, [order, params]);
  const copyPixCode = () => {
    if (!pix) return;
    navigator.clipboard.writeText(pix.text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); });
  };
  return <section className="section confirmation"><div className="confirmation-card">{status === "checking" ? <><span className="spinner" /><h1>Confirmando pagamento...</h1></> : status === "paid" ? <><div className="success-icon"><Check /></div><span className="eyebrow">Pagamento aprovado</span><h1>Pedido confirmado!</h1><p>O pedido <b>{order}</b> já entrou em preparação. Enviaremos as atualizações pelo contato informado.</p></> : status === "demo" ? <><div className="demo-icon"><Settings /></div><span className="eyebrow">Pedido de demonstração</span><h1>O fluxo está funcionando</h1><p>Pedido <b>{order}</b> criado. {message}</p><Notice>Para liberar cobranças reais, configure o token do PagBank no Worker.</Notice></> : pix ? <><div className="pending-icon"><Zap /></div><span className="eyebrow">Pedido recebido</span><h1>Pague com Pix para confirmar</h1><p>Pedido <b>{order}</b>. Escaneie o QR Code ou copie o código abaixo no app do seu banco.</p>{pix.image_base64 && <img className="pix-qr-image" src={`data:image/png;base64,${pix.image_base64}`} alt="QR Code Pix" />}<textarea className="pix-copy-paste" readOnly value={pix.text} onFocus={(event) => event.target.select()} /><button type="button" className="button button-gold" onClick={copyPixCode}>{copied ? "Código copiado!" : "Copiar código Pix"}</button><p className="pix-waiting"><span className="spinner small" /> Aguardando confirmação automática...</p></> : <><div className="pending-icon"><Clock3 /></div><span className="eyebrow">Pedido recebido</span><h1>Aguardando confirmação</h1><p>Pedido <b>{order}</b>. Assim que o PagBank confirmar, o status será atualizado automaticamente.</p>{message && <Notice>{message}</Notice>}</>}
    <div className="confirmation-actions"><Link className="button button-gold" to="/produtos">Continuar comprando</Link><a className="button button-outline" href={`https://wa.me/${settings.whatsapp}`} target="_blank" rel="noreferrer">Falar no WhatsApp</a></div></div></section>;
}

function Policies({ settings }: { settings: StoreSettings }) {
  return <section className="section legal-page"><div className="container narrow"><span className="eyebrow">Transparência e cuidado</span><h1>Políticas da loja</h1><article><h2>Trocas e devoluções</h2><p>Você pode solicitar a devolução de compras online em até 7 dias corridos após o recebimento, conforme o direito de arrependimento previsto na legislação brasileira. O produto deve estar sem sinais de uso, com acessórios e embalagem recebidos.</p><p>Para troca por tamanho, modelo ou eventual defeito, fale conosco pelo WhatsApp informando o número do pedido e enviando fotos do produto. Analisaremos a solicitação e orientaremos sobre o envio.</p><h2>Envios</h2><p>Os pedidos são preparados para postagem em até {settings.posting_days} dias úteis após a confirmação do pagamento. O prazo de transporte depende do CEP e da modalidade escolhida.</p><h2>Privacidade</h2><p>Nome, contato e endereço são utilizados para processar, entregar e prestar suporte ao pedido. Os dados de cartão não passam pelos nossos servidores: o pagamento é processado no ambiente seguro do PagBank.</p><h2>Contato</h2><p>Dúvidas podem ser enviadas para {settings.email} ou pelo WhatsApp (11) 99992-8524, durante {settings.business_hours.toLowerCase()}.</p></article></div></section>;
}

type AdminTab = "dashboard" | "products" | "orders" | "chats" | "videos" | "analytics" | "users" | "settings";

function AdminPage({ settings: publicSettings, reloadStore }: { settings: StoreSettings; reloadStore: () => Promise<void> }) {
  const { user, refreshUser } = useStore();
  const [tab, setTab] = useState<AdminTab>("dashboard");
  const [setup, setSetup] = useState<{ needs_setup: boolean; admin_email: string } | null>(null);
  const [auth, setAuth] = useState({ email: "poncell25@gmail.com", name: "Administrador", password: "", setup_token: "" });
  const [message, setMessage] = useState("");
  useEffect(() => { api.get<{ needs_setup: boolean; admin_email: string }>("/api/auth/setup-status").then(setSetup); }, []);
  const authenticate = async (event: FormEvent) => {
    event.preventDefault(); setMessage("");
    try {
      if (setup?.needs_setup) await api.post("/api/auth/admin-setup", auth);
      else await api.post("/api/auth/login", { email: auth.email, password: auth.password });
      await refreshUser();
    } catch (caught) { setMessage(caught instanceof Error ? caught.message : "Não foi possível entrar."); }
  };
  if (!user || user.role !== "admin") return <section className="section admin-login"><div className="auth-card"><Logo /><span className="eyebrow">Área administrativa</span><h1>{setup?.needs_setup ? "Configuração inicial" : "Entrar no painel"}</h1><p>{setup?.needs_setup ? `Crie a senha do administrador ${setup.admin_email}.` : "Use seu acesso administrativo."}</p><form onSubmit={authenticate}>{setup?.needs_setup && <><label>Nome<input value={auth.name} onChange={(event) => setAuth({ ...auth, name: event.target.value })} /></label><label>Código inicial<input required value={auth.setup_token} onChange={(event) => setAuth({ ...auth, setup_token: event.target.value })} /></label></>}<label>E-mail<input type="email" required value={auth.email} onChange={(event) => setAuth({ ...auth, email: event.target.value })} /></label><label>Senha<input type="password" minLength={8} required value={auth.password} onChange={(event) => setAuth({ ...auth, password: event.target.value })} /></label>{message && <Notice tone="danger">{message}</Notice>}<button className="button button-gold button-full">{setup?.needs_setup ? "Criar acesso" : "Entrar"}</button></form></div></section>;
  const tabs: Array<[AdminTab, string, ReactNode]> = [["dashboard", "Visão geral", <LayoutDashboard />], ["products", "Produtos", <Package />], ["orders", "Pedidos", <ShoppingBag />], ["chats", "Chats", <MessageSquare />], ["videos", "Vídeos", <Video />], ["analytics", "Estatísticas", <BarChart3 />], ["users", "Administradores", <Users />], ["settings", "Configurações", <Settings />]];
  return <><WeeklyReportPopup /><section className="admin-page"><aside className="admin-sidebar"><Logo /><nav>{tabs.map(([key, label, icon]) => <button key={key} className={tab === key ? "active" : ""} onClick={() => setTab(key)}>{icon}{label}</button>)}</nav><Link to="/" className="back-store"><Store /> Ver loja</Link></aside><div className="admin-content"><header><div><span>Painel administrativo</span><h1>{tabs.find(([key]) => key === tab)?.[1]}</h1></div><div className="admin-user"><span>{user.name}</span><CircleUserRound /></div></header>{tab === "dashboard" && <AdminDashboard />}{tab === "products" && <AdminProducts />}{tab === "orders" && <AdminOrders />}{tab === "chats" && <AdminChats />}{tab === "videos" && <AdminVideos />}{tab === "analytics" && <AdminAnalytics />}{tab === "users" && <AdminUsers />}{tab === "settings" && <AdminSettings initial={publicSettings} onSaved={reloadStore} />}</div></section></>;
}

const formatReportDate = (value: string) => new Date(value).toLocaleDateString("pt-BR", { timeZone: "UTC" });

function WeeklyReportPopup() {
  const [report, setReport] = useState<WeeklyReport | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const now = new Date();
    const daysSinceMonday = (now.getDay() + 6) % 7;
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday);
    const weekKey = monday.toISOString().slice(0, 10);
    const dueTime = !(now.getDay() === 1 && now.getHours() < 9);
    const alreadyShown = localStorage.getItem("weeklyReportShown") === weekKey;
    if (!dueTime || alreadyShown) return;
    api.get<WeeklyReport>("/api/admin/reports/weekly").then((data) => {
      setReport(data);
      setVisible(true);
      localStorage.setItem("weeklyReportShown", weekKey);
    }).catch(() => { /* sem relatório, sem popup */ });
  }, []);
  if (!visible || !report) return null;
  return <div className="modal-backdrop"><div className="admin-modal weekly-popup">
    <div className="admin-card-title"><div><small>Resumo da semana passada</small><h2>{formatReportDate(report.week_start)} a {formatReportDate(report.week_end)}</h2></div><button type="button" className="icon-button" onClick={() => setVisible(false)}><X /></button></div>
    <div className="weekly-popup-body">
      <div><span>Faturamento da semana</span><strong>{money(report.total_cents)}</strong></div>
      <div><span>Pedidos pagos</span><strong>{report.order_count}</strong></div>
      <div className="weekly-popup-highlight"><span>Repasse ({report.commission_percent}%) para Wagner Poncell</span><strong>{money(report.commission_cents)}</strong></div>
    </div>
    <div className="modal-actions"><button type="button" className="button button-gold" onClick={() => setVisible(false)}>Entendi</button></div>
  </div></div>;
}

function AdminDashboard() {
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [report, setReport] = useState<WeeklyReport | null>(null);
  const load = () => Promise.all([api.get<{ products: Product[] }>("/api/admin/products"), api.get<{ orders: Order[] }>("/api/admin/orders")]).then(([p, o]) => { setProducts(p.products); setOrders(o.orders); });
  useEffect(() => { void load(); api.get<WeeklyReport>("/api/admin/reports/weekly?period=current").then(setReport).catch(() => {}); }, []);
  const paid = orders.filter((order) => order.payment_status === "paid");
  return <div className="admin-panel"><div className="metric-grid"><div><Package /><span><small>Produtos ativos</small><strong>{products.filter((product) => product.active).length}</strong></span></div><div><ShoppingBag /><span><small>Total de pedidos</small><strong>{orders.length}</strong></span></div><div><CreditCard /><span><small>Pedidos pagos</small><strong>{paid.length}</strong></span></div><div><Zap /><span><small>Valor vendido</small><strong>{money(paid.reduce((sum, order) => sum + order.total_cents, 0))}</strong></span></div></div>
    {report && <div className="admin-card weekly-report-card"><div className="admin-card-title"><div><h2>Faturamento desta semana</h2><p>{formatReportDate(report.week_start)} a hoje</p></div></div><div className="weekly-popup-body"><div><span>Faturamento da semana (até agora)</span><strong>{money(report.total_cents)}</strong></div><div><span>Pedidos pagos</span><strong>{report.order_count}</strong></div><div className="weekly-popup-highlight"><span>Repasse ({report.commission_percent}%) para Wagner Poncell</span><strong>{money(report.commission_cents)}</strong></div></div></div>}
    <div className="admin-card"><div className="admin-card-title"><h2>Pedidos recentes</h2></div><AdminOrderTable orders={orders.slice(0, 6)} onChange={load} compact /></div></div>;
}

const PRODUCT_CATEGORIES = ["Tênis", "Camisetas", "Calças", "Blusas de frio", "Bonés", "Perfumes", "Cintos", "Acessórios"];

const blankProduct: Product = { id: "", slug: "", name: "", category: "Tênis", description: "", price_cents: 0, compare_at_cents: null, sizes: [], colors: [], stock: 1, weight_grams: null, dimensions: null, images: [], featured: false, active: true };

function AdminProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [editing, setEditing] = useState<Product | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const load = () => api.get<{ products: Product[] }>("/api/admin/products").then((data) => setProducts(data.products));
  useEffect(() => { void load(); }, []);
  const visibleProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products
      .filter((product) => !categoryFilter || product.category === categoryFilter)
      .filter((product) => !term || product.name.toLowerCase().includes(term) || product.slug.toLowerCase().includes(term))
      .sort((a, b) => Number(b.active) - Number(a.active));
  }, [products, search, categoryFilter]);
  const save = async (event: FormEvent) => {
    event.preventDefault(); if (!editing) return; setSaving(true); setMessage("");
    try {
      editing.id ? await api.patch(`/api/admin/products/${editing.id}`, editing) : await api.post("/api/admin/products", editing);
      await load(); setEditing(null);
    } catch (caught) { setMessage(caught instanceof Error ? caught.message : "Erro ao salvar."); } finally { setSaving(false); }
  };
  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0] || !editing) return;
    setSaving(true);
    try { const result = await api.upload<{ url: string }>("/api/admin/media", event.target.files[0]); setEditing({ ...editing, images: [...editing.images, result.url] }); }
    catch (caught) { setMessage(caught instanceof Error ? caught.message : "Erro no upload."); } finally { setSaving(false); }
  };
  const remove = async (product: Product) => {
    if (!window.confirm(`Excluir "${product.name}" permanentemente? Essa ação não pode ser desfeita.`)) return;
    try { await api.delete(`/api/admin/products/${product.id}`); await load(); }
    catch (caught) { setMessage(caught instanceof Error ? caught.message : "Erro ao excluir."); }
  };
  return <div className="admin-panel"><div className="admin-toolbar"><div><h2>Catálogo</h2><p>{visibleProducts.length} de {products.length} produtos cadastrados</p></div><button className="button button-gold" onClick={() => setEditing({ ...blankProduct })}><Plus /> Novo produto</button></div>{message && <Notice tone="danger">{message}</Notice>}
    <div className="admin-filters">
      <label className="admin-search"><Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por nome ou slug..." /></label>
      <div className="category-filter">
        <button type="button" className={categoryFilter === "" ? "category-chip active" : "category-chip"} onClick={() => setCategoryFilter("")}>Todos</button>
        {PRODUCT_CATEGORIES.map((category) => <button key={category} type="button" className={categoryFilter === category ? "category-chip active" : "category-chip"} onClick={() => setCategoryFilter(category)}>{category}</button>)}
      </div>
    </div>
    <div className="admin-card table-wrap"><table><thead><tr><th>Produto</th><th>Categoria</th><th>Preço</th><th>Estoque</th><th>Status</th><th /></tr></thead><tbody>{visibleProducts.map((product) => <tr key={product.id}><td><div className="table-product"><img src={product.images[0]} alt="" /><span><b>{product.name}</b><small>{product.slug}</small></span></div></td><td>{product.category}</td><td>{money(product.price_cents)}</td><td>{product.stock ?? "A confirmar"}</td><td><span className={`status ${product.active ? "status-active" : "status-cancelled"}`}>{product.active ? "Ativo" : "Oculto"}</span></td><td><button className="table-action" title="Editar" onClick={() => setEditing({ ...product })}><Edit3 /></button><button className="table-action table-action-danger" title="Excluir" onClick={() => remove(product)}><Trash2 /></button></td></tr>)}</tbody></table></div>
    {editing && <div className="modal-backdrop"><form className="admin-modal" onSubmit={save}><div className="admin-card-title"><div><small>{editing.id ? "Editar produto" : "Novo produto"}</small><h2>{editing.name || "Produto sem nome"}</h2></div><button type="button" className="icon-button" onClick={() => setEditing(null)}><X /></button></div><div className="product-editor">
      <label className="span-2">Nome<input required value={editing.name} onChange={(event) => setEditing({ ...editing, name: event.target.value })} /></label>
      <label>Categoria<select value={editing.category} onChange={(event) => setEditing({ ...editing, category: event.target.value })}>{PRODUCT_CATEGORIES.map((category) => <option key={category}>{category}</option>)}</select></label>
      <label>Slug<input value={editing.slug} onChange={(event) => setEditing({ ...editing, slug: event.target.value })} placeholder="gerado automaticamente" /></label>
      <label className="span-2">Descrição<textarea value={editing.description} onChange={(event) => setEditing({ ...editing, description: event.target.value })} /></label>
      <label>Preço (R$)<input type="number" step=".01" value={editing.price_cents / 100} onChange={(event) => setEditing({ ...editing, price_cents: Math.round(Number(event.target.value) * 100) })} /></label>
      <label>Preço anterior (R$)<input type="number" step=".01" value={(editing.compare_at_cents || 0) / 100} onChange={(event) => setEditing({ ...editing, compare_at_cents: Number(event.target.value) ? Math.round(Number(event.target.value) * 100) : null })} /></label>
      <label>Tamanhos (separados por vírgula)<input value={editing.sizes.join(", ")} onChange={(event) => setEditing({ ...editing, sizes: event.target.value.split(",").map((item) => item.trim()).filter(Boolean) })} /></label>
      <label>Cores (separadas por vírgula)<input value={editing.colors.join(", ")} onChange={(event) => setEditing({ ...editing, colors: event.target.value.split(",").map((item) => item.trim()).filter(Boolean) })} /></label>
      <label>Estoque<input type="number" value={editing.stock ?? ""} onChange={(event) => setEditing({ ...editing, stock: event.target.value === "" ? null : Number(event.target.value) })} /></label>
      <label>Peso (gramas)<input type="number" value={editing.weight_grams ?? ""} onChange={(event) => setEditing({ ...editing, weight_grams: event.target.value ? Number(event.target.value) : null })} /></label>
      <div className="span-2 image-editor"><label className="upload-button"><Upload /> Adicionar foto<input hidden type="file" accept="image/*" onChange={upload} /></label><div>{editing.images.map((image, index) => <div key={image}><img src={image} alt="" /><button type="button" onClick={() => setEditing({ ...editing, images: editing.images.filter((_, itemIndex) => itemIndex !== index) })}><X /></button></div>)}</div></div>
      <label className="check-label"><input type="checkbox" checked={editing.featured} onChange={(event) => setEditing({ ...editing, featured: event.target.checked })} /> Produto em destaque</label><label className="check-label"><input type="checkbox" checked={editing.active} onChange={(event) => setEditing({ ...editing, active: event.target.checked })} /> Produto visível</label>
    </div>{message && <Notice tone="danger">{message}</Notice>}<div className="modal-actions"><button type="button" className="button button-outline" onClick={() => setEditing(null)}>Cancelar</button><button className="button button-gold" disabled={saving}>{saving ? "Salvando..." : "Salvar produto"}</button></div></form></div>}
  </div>;
}

const paymentStatusLabel: Record<string, string> = {
  pending: "Pendente",
  paid: "Pago",
  refunded: "Reembolsado",
  cancelled: "Cancelado"
};

function AdminOrderTable({ orders, onChange, compact = false }: { orders: Order[]; onChange: () => void | Promise<void>; compact?: boolean }) {
  const [savingId, setSavingId] = useState("");
  const [message, setMessage] = useState("");
  const update = async (order: Order, patch: Partial<Pick<Order, "order_status" | "payment_status">>) => {
    setSavingId(order.id); setMessage("");
    try {
      await api.patch(`/api/admin/orders/${order.id}`, patch);
      await onChange();
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Não foi possível alterar o pedido.");
    } finally {
      setSavingId("");
    }
  };
  if (!orders.length) return <div className="empty-state panel"><ShoppingBag /><p>Nenhum pedido ainda.</p></div>;
  const itemsLabel = (order: Order) => order.items.map((item) => `${item.quantity}x ${item.name}${item.size ? ` (${item.size})` : ""}${item.color ? ` ${item.color}` : ""}`).join(", ");
  return <>{message && <Notice tone="danger">{message}</Notice>}<div className="table-wrap"><table><thead><tr><th>Pedido</th><th>Cliente</th><th>Produtos</th><th>Total</th><th>Pagamento</th><th>Status do pedido</th>{!compact && <th>Data</th>}</tr></thead><tbody>{orders.map((order) => <tr key={order.id}><td><b>{order.order_code}</b>{savingId === order.id && <small className="table-url">Salvando...</small>}</td><td><span className="table-customer"><b>{order.customer_name}</b><small>{order.customer_email}</small></span></td><td className="table-items" title={itemsLabel(order)}>{itemsLabel(order)}</td><td>{money(order.total_cents)}</td><td><select className={`status-select status-${order.payment_status}`} value={order.payment_status} onChange={(event) => void update(order, { payment_status: event.target.value })} disabled={savingId === order.id}><option value="pending">Pendente</option><option value="paid">Pago</option><option value="refunded">Reembolsado</option><option value="cancelled">Cancelado</option></select><span className={`status status-${order.payment_status}`}>{paymentStatusLabel[order.payment_status] || order.payment_status}</span></td><td><select value={order.order_status} onChange={(event) => void update(order, { order_status: event.target.value })} disabled={savingId === order.id}><option value="awaiting_payment">Aguardando pagamento</option><option value="preparing">Em preparação</option><option value="shipped">Enviado</option><option value="ready_for_pickup">Pronto para retirada</option><option value="completed">Concluído</option><option value="cancelled">Cancelado</option></select></td>{!compact && <td>{new Date(order.created_at).toLocaleDateString("pt-BR")}</td>}</tr>)}</tbody></table></div></>;
}

function AdminOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const load = () => api.get<{ orders: Order[] }>("/api/admin/orders").then((data) => setOrders(data.orders));
  useEffect(() => { void load(); }, []);
  return <div className="admin-panel"><div className="admin-toolbar"><div><h2>Pedidos</h2><p>Acompanhe pagamento, separação e entrega.</p></div><button className="button button-outline" onClick={() => void load()}>Atualizar</button></div><div className="admin-card"><AdminOrderTable orders={orders} onChange={load} /></div></div>;
}

function AdminChats() {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selected, setSelected] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [reply, setReply] = useState("");
  const [message, setMessage] = useState("");
  const load = () => api.get<{ conversations: ChatConversation[] }>("/api/admin/chats").then((data) => setConversations(data.conversations));
  const open = async (conversation: ChatConversation) => {
    const data = await api.get<{ conversation: ChatConversation; messages: ChatMessage[] }>(`/api/admin/chats/${conversation.id}/messages`);
    setSelected(data.conversation); setMessages(data.messages);
  };
  useEffect(() => { void load(); }, []);
  const send = async (event: FormEvent) => {
    event.preventDefault(); if (!selected || !reply.trim()) return;
    await api.post(`/api/admin/chats/${selected.id}/messages`, { message: reply });
    setReply(""); await open(selected); await load();
  };
  const changeStatus = async (status: "open" | "closed") => {
    if (!selected) return;
    await api.patch(`/api/admin/chats/${selected.id}`, { status });
    setMessage(status === "closed" ? "Conversa encerrada." : "Conversa reaberta.");
    await open({ ...selected, status }); await load();
  };
  return <div className="admin-panel"><div className="admin-toolbar"><div><h2>Chats</h2><p>Responda mensagens enviadas pelo site.</p></div><button className="button button-outline" onClick={() => void load()}>Atualizar</button></div><div className="admin-chat-layout">
    <div className="admin-card chat-list">{conversations.length ? conversations.map((conversation) => <button key={conversation.id} className={selected?.id === conversation.id ? "active" : ""} onClick={() => void open(conversation)}><b>{conversation.customer_name}</b><small>{conversation.customer_phone || conversation.customer_email || "Sem contato"}</small><span className={`status status-${conversation.status === "open" ? "pending" : "completed"}`}>{conversation.status === "open" ? "Aberto" : "Fechado"}</span></button>) : <div className="empty-state panel"><MessageSquare /><p>Nenhum chat ainda.</p></div>}</div>
    <div className="admin-card chat-admin-card">{selected ? <><div className="admin-card-title"><div><h2>{selected.customer_name}</h2><p>{selected.customer_phone || selected.customer_email || "Contato não informado"}</p></div><button className="button button-outline" onClick={() => void changeStatus(selected.status === "open" ? "closed" : "open")}>{selected.status === "open" ? "Encerrar" : "Reabrir"}</button></div><div className="chat-messages admin">{messages.map((item) => <div key={item.id} className={`chat-bubble ${item.sender_type}`}><small>{item.sender_name} • {new Date(item.created_at).toLocaleString("pt-BR")}</small><p>{item.body}</p></div>)}</div><form className="chat-send admin" onSubmit={send}><input value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Responder cliente..." /><button disabled={!reply.trim()}><Send size={17} /></button></form>{message && <Notice>{message}</Notice>}</> : <div className="empty-state"><MessageSquare /><h2>Selecione uma conversa</h2><p>As mensagens do cliente aparecem aqui.</p></div>}</div>
  </div></div>;
}

const blankVideo: StoreVideo = { id: "", title: "", description: "", source_type: "url", url: "", poster_url: "", active: true, sort_order: 0 };

function AdminVideos() {
  const [videos, setVideos] = useState<StoreVideo[]>([]);
  const [editing, setEditing] = useState<StoreVideo | null>(null);
  const [message, setMessage] = useState("");
  const load = () => api.get<{ videos: StoreVideo[] }>("/api/admin/videos").then((data) => setVideos(data.videos));
  useEffect(() => { void load(); }, []);
  const save = async (event: FormEvent) => {
    event.preventDefault(); if (!editing) return; setMessage("");
    try {
      editing.id ? await api.patch(`/api/admin/videos/${editing.id}`, editing) : await api.post("/api/admin/videos", editing);
      await load(); setEditing(null);
    } catch (caught) { setMessage(caught instanceof Error ? caught.message : "Erro ao salvar vídeo."); }
  };
  const upload = (field: "url" | "poster_url") => async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0] || !editing) return;
    try { const result = await api.upload<{ url: string }>("/api/admin/media", event.target.files[0]); setEditing({ ...editing, [field]: result.url, source_type: field === "url" ? "upload" : editing.source_type }); }
    catch (caught) { setMessage(caught instanceof Error ? caught.message : "Erro no upload."); }
  };
  return <div className="admin-panel"><div className="admin-toolbar"><div><h2>Vídeos</h2><p>Adicione YouTube, Vimeo, MP4, WebM, OGG ou upload pequeno.</p></div><button className="button button-gold" onClick={() => setEditing({ ...blankVideo })}><Plus /> Novo vídeo</button></div><div className="admin-card table-wrap"><table><thead><tr><th>Vídeo</th><th>Formato</th><th>Ordem</th><th>Status</th><th /></tr></thead><tbody>{videos.map((video) => <tr key={video.id}><td><b>{video.title}</b><small className="table-url">{video.url}</small></td><td>{video.source_type}</td><td>{video.sort_order}</td><td><span className={`status ${video.active ? "status-active" : "status-cancelled"}`}>{video.active ? "Ativo" : "Oculto"}</span></td><td><button className="table-action" onClick={() => setEditing({ ...video })}><Edit3 /></button></td></tr>)}</tbody></table></div>
    {editing && <div className="modal-backdrop"><form className="admin-modal" onSubmit={save}><div className="admin-card-title"><div><small>{editing.id ? "Editar vídeo" : "Novo vídeo"}</small><h2>{editing.title || "Vídeo sem título"}</h2></div><button type="button" className="icon-button" onClick={() => setEditing(null)}><X /></button></div><div className="product-editor">
      <label className="span-2">Título<input required value={editing.title} onChange={(event) => setEditing({ ...editing, title: event.target.value })} /></label>
      <label>Formato<select value={editing.source_type} onChange={(event) => setEditing({ ...editing, source_type: event.target.value as StoreVideo["source_type"] })}><option value="url">Arquivo/link direto</option><option value="youtube">YouTube</option><option value="vimeo">Vimeo</option><option value="upload">Upload</option></select></label>
      <label>Ordem<input type="number" value={editing.sort_order} onChange={(event) => setEditing({ ...editing, sort_order: Number(event.target.value) })} /></label>
      <label className="span-2">URL do vídeo<input required value={editing.url} onChange={(event) => setEditing({ ...editing, url: event.target.value })} placeholder="https://... ou /media/videos/..." /></label>
      <label className="upload-button"><Upload /> Enviar vídeo<input hidden type="file" accept="video/mp4,video/webm,video/ogg" onChange={upload("url")} /></label>
      <label className="span-2">Descrição<textarea value={editing.description} onChange={(event) => setEditing({ ...editing, description: event.target.value })} /></label>
      <label className="span-2">Capa/poster<input value={editing.poster_url || ""} onChange={(event) => setEditing({ ...editing, poster_url: event.target.value })} /></label>
      <label className="upload-button"><Upload /> Enviar capa<input hidden type="file" accept="image/*" onChange={upload("poster_url")} /></label>
      <label className="check-label"><input type="checkbox" checked={editing.active} onChange={(event) => setEditing({ ...editing, active: event.target.checked })} /> Vídeo visível</label>
    </div>{message && <Notice tone="danger">{message}</Notice>}<div className="modal-actions"><button type="button" className="button button-outline" onClick={() => setEditing(null)}>Cancelar</button><button className="button button-gold">Salvar vídeo</button></div></form></div>}
  </div>;
}

function AdminAnalytics() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get<AnalyticsSummary>(`/api/admin/analytics/summary?days=${days}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [days]);
  const locationLabel = (item: { country: string; city: string | null }) =>
    [item.city, item.country].filter(Boolean).join(" - ");
  return <div className="admin-panel">
    <div className="admin-toolbar"><div><h2>Estatísticas do site</h2><p>Acessos, cliques e origem dos visitantes.</p></div>
      <div className="category-filter">
        {[7, 30, 90].map((option) => <button key={option} type="button" className={days === option ? "category-chip active" : "category-chip"} onClick={() => setDays(option)}>Últimos {option} dias</button>)}
      </div>
    </div>
    {loading || !data ? <div className="empty-state panel"><BarChart3 /><p>Carregando estatísticas...</p></div> : <>
      <div className="metric-grid">
        <div><Globe /><span><small>Visitas às páginas</small><strong>{data.pageviews}</strong></span></div>
        <div><MousePointerClick /><span><small>Cliques rastreados</small><strong>{data.clicks}</strong></span></div>
      </div>
      <div className="analytics-grid">
        <div className="admin-card"><div className="admin-card-title"><h2>Páginas mais visitadas</h2></div><div className="analytics-list">
          {data.top_paths.length ? data.top_paths.map((item) => <div key={item.path}><span>{item.path || "/"}</span><strong>{item.total}</strong></div>) : <p className="analytics-empty">Sem dados ainda.</p>}
        </div></div>
        <div className="admin-card"><div className="admin-card-title"><h2>De onde vieram (referência)</h2></div><div className="analytics-list">
          {data.top_referrers.length ? data.top_referrers.map((item) => <div key={item.referrer_host}><span>{item.referrer_host}</span><strong>{item.total}</strong></div>) : <p className="analytics-empty">Sem dados ainda.</p>}
        </div></div>
        <div className="admin-card"><div className="admin-card-title"><h2>Localização dos acessos</h2></div><div className="analytics-list">
          {data.top_locations.length ? data.top_locations.map((item, index) => <div key={index}><span>{locationLabel(item) || "Desconhecido"}</span><strong>{item.total}</strong></div>) : <p className="analytics-empty">Sem dados ainda.</p>}
        </div></div>
        <div className="admin-card"><div className="admin-card-title"><h2>Cliques mais comuns</h2></div><div className="analytics-list">
          {data.top_clicks.length ? data.top_clicks.map((item) => <div key={item.label}><span>{item.label}</span><strong>{item.total}</strong></div>) : <p className="analytics-empty">Sem dados ainda.</p>}
        </div></div>
      </div>
    </>}
  </div>;
}

function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [passwords, setPasswords] = useState<Record<string, string>>({});
  const [message, setMessage] = useState("");
  const load = () => api.get<{ users: AdminUser[] }>("/api/admin/users").then((data) => setUsers(data.users));
  useEffect(() => { void load(); }, []);
  const create = async (event: FormEvent) => {
    event.preventDefault(); setMessage("");
    try { await api.post("/api/admin/users", form); setForm({ name: "", email: "", phone: "", password: "" }); await load(); setMessage("Administrador criado."); }
    catch (caught) { setMessage(caught instanceof Error ? caught.message : "Erro ao criar administrador."); }
  };
  const changePassword = async (userId: string) => {
    const password = passwords[userId] || "";
    if (password.length < 8) { setMessage("A nova senha precisa ter pelo menos 8 caracteres."); return; }
    await api.patch(`/api/admin/users/${userId}/password`, { password });
    setPasswords({ ...passwords, [userId]: "" });
    setMessage("Senha alterada. Esse administrador precisará entrar novamente.");
  };
  return <div className="admin-panel admin-users-grid"><form className="admin-card settings-form" onSubmit={create}><div className="admin-card-title"><div><h2>Novo administrador</h2><p>Crie outro usuário com acesso ao painel.</p></div></div><div className="form-grid"><label>Nome<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label><label>E-mail<input required type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label><label>WhatsApp<input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label><label>Senha inicial<input required minLength={8} type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} /></label></div><button className="button button-gold">Criar administrador</button>{message && <Notice tone={message.includes("criado") || message.includes("alterada") ? "success" : "danger"}>{message}</Notice>}</form>
    <div className="admin-card table-wrap"><div className="admin-card-title"><div><h2>Administradores</h2><p>Altere senhas quando precisar.</p></div><KeyRound /></div><table><thead><tr><th>Nome</th><th>E-mail</th><th>Nova senha</th><th /></tr></thead><tbody>{users.map((user) => <tr key={user.id}><td>{user.name}</td><td>{user.email}</td><td><input type="password" value={passwords[user.id] || ""} onChange={(event) => setPasswords({ ...passwords, [user.id]: event.target.value })} placeholder="mín. 8 caracteres" /></td><td><button className="table-action" onClick={() => void changePassword(user.id)}><KeyRound /></button></td></tr>)}</tbody></table></div>
  </div>;
}

function AdminSettings({ initial, onSaved }: { initial: StoreSettings; onSaved: () => Promise<void> }) {
  const [settings, setSettings] = useState(initial);
  const [message, setMessage] = useState("");
  const save = async (event: FormEvent) => { event.preventDefault(); try { await api.put("/api/admin/settings", settings); await onSaved(); setMessage("Configurações salvas."); } catch (caught) { setMessage(caught instanceof Error ? caught.message : "Erro ao salvar."); } };
  const text = (key: keyof StoreSettings) => (event: ChangeEvent<HTMLInputElement>) => setSettings({ ...settings, [key]: event.target.value });
  const number = (key: keyof StoreSettings, cents = false) => (event: ChangeEvent<HTMLInputElement>) => setSettings({ ...settings, [key]: cents ? Math.round(Number(event.target.value) * 100) : Number(event.target.value) });
  return <div className="admin-panel"><form className="admin-card settings-form" onSubmit={save}><div className="admin-card-title"><div><h2>Dados da loja</h2><p>Altere contatos, benefícios e regras de entrega.</p></div></div><div className="form-grid"><label>Nome da loja<input value={settings.store_name} onChange={text("store_name")} /></label><label>Slogan<input value={settings.slogan} onChange={text("slogan")} /></label><label>WhatsApp<input value={settings.whatsapp} onChange={text("whatsapp")} /></label><label>Instagram<input value={settings.instagram} onChange={text("instagram")} /></label><label>E-mail<input value={settings.email} onChange={text("email")} /></label><label>Localização<input value={settings.address} onChange={text("address")} /></label><label className="span-2">Horário<input value={settings.business_hours} onChange={text("business_hours")} /></label></div><hr /><h3>Pagamento e descontos</h3><div className="form-grid"><label>Desconto Pix (%)<input type="number" value={settings.pix_discount_percent} onChange={number("pix_discount_percent")} /></label><label>Máximo de parcelas<input type="number" value={settings.installments} onChange={number("installments")} /></label><label>Cupom inicial<input value={settings.first_coupon_code} onChange={text("first_coupon_code")} /></label><label>Desconto do cupom (%)<input type="number" value={settings.first_coupon_percent} onChange={number("first_coupon_percent")} /></label></div><hr /><h3>Entrega</h3><div className="form-grid"><label>Frete nacional (R$)<input type="number" step=".01" value={settings.shipping_national_cents / 100} onChange={number("shipping_national_cents", true)} /></label><label>Entrega local (R$)<input type="number" step=".01" value={settings.shipping_local_cents / 100} onChange={number("shipping_local_cents", true)} /></label><label>Frete grátis acima de (R$)<input type="number" step=".01" value={settings.free_shipping_from_cents / 100} onChange={number("free_shipping_from_cents", true)} /></label><label>Prazo de postagem (dias úteis)<input type="number" value={settings.posting_days} onChange={number("posting_days")} /></label></div>{message && <Notice tone={message.includes("salvas") ? "success" : "danger"}>{message}</Notice>}<button className="button button-gold">Salvar configurações</button></form></div>;
}

function App() {
  const { products, videos, settings, loading, error, reload } = useCatalog();
  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="loading-page">
        <p>Não foi possível carregar a loja. Verifique sua conexão e tente novamente.</p>
        <button className="button button-gold" onClick={() => void reload()}>Tentar novamente</button>
      </div>
    );
  }
  return (
    <Routes>
      <Route path="/admin" element={<AdminPage settings={settings} reloadStore={reload} />} />
      <Route element={<Shell settings={settings} />}>
        <Route path="/" element={<Home products={products} settings={settings} videos={videos} />} />
        <Route path="/produtos" element={<Catalog products={products} />} />
        <Route path="/produto/:slug" element={<ProductDetail products={products} settings={settings} />} />
        <Route path="/carrinho" element={<CartPage />} />
        <Route path="/checkout" element={<Checkout settings={settings} />} />
        <Route path="/entrar" element={<AuthPage />} />
        <Route path="/minha-conta" element={<AccountPage />} />
        <Route path="/pedido-confirmado" element={<ConfirmationPage settings={settings} />} />
        <Route path="/politicas" element={<Policies settings={settings} />} />
        <Route path="*" element={<section className="container section empty-state"><h1>Página não encontrada</h1><Link className="button button-gold" to="/">Voltar ao início</Link></section>} />
      </Route>
    </Routes>
  );
}

export default App;
